import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  Calendar, 
  BookOpen, 
  Heart, 
  Users, 
  Award, 
  Clock, 
  Star, 
  Zap, 
  TrendingUp, 
  BarChart2, 
  Activity, 
  Target, 
  Compass,
  MessageCircle,
  PenTool,
  CheckCircle,
  Flame
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface JourneyStats {
  totalInteractions: number;
  studyTime: number; // em minutos
  prayerTime: number; // em minutos
  challengesCompleted: number;
  totalAchievements: number;
  devotionalStreak: number;
  communityEngagement: number;
  bibleReadingProgress: number;
  prayerRequests: number;
  testimonies: number;
  quizCompleted: number;
  coursesCompleted: number;
  smallGroupAttendance: number;
  spiritualGrowthScore: number;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  date: string;
  color: string;
}

interface Activity {
  id: string;
  type: string;
  title: string;
  date: string;
  icon: React.ElementType;
  color: string;
}

export default function FaithJourney() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<JourneyStats>({
    totalInteractions: 0,
    studyTime: 0,
    prayerTime: 0,
    challengesCompleted: 0,
    totalAchievements: 0,
    devotionalStreak: 0,
    communityEngagement: 0,
    bibleReadingProgress: 0,
    prayerRequests: 0,
    testimonies: 0,
    quizCompleted: 0,
    coursesCompleted: 0,
    smallGroupAttendance: 0,
    spiritualGrowthScore: 0
  });
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [userProfile, setUserProfile] = useState({
    name: '',
    email: '',
    createdAt: '',
    churchName: ''
  });
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year' | 'all'>('month');

  useEffect(() => {
    checkAuthStatus();
    fetchUserData();
  }, []);

  useEffect(() => {
    fetchJourneyStats();
  }, [timeRange]);

  async function checkAuthStatus() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/auth', { state: { returnTo: '/faith-journey' } });
    }
  }

  async function fetchUserData() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Obter dados do perfil
      const userName = user.user_metadata?.name || user.email?.split('@')[0] || 'Usuário';
      const userEmail = user.email || '';
      const createdAt = user.created_at ? format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR }) : '';
      
      // Obter igreja do usuário
      const { data: userData } = await supabase
        .from('users')
        .select('igreja_id')
        .eq('id', user.id)
        .single();
      
      let churchName = 'Igreja não definida';
      if (userData?.igreja_id) {
        const { data: churchData } = await supabase
          .from('igrejas')
          .select('nome')
          .eq('id', userData.igreja_id)
          .single();
        
        if (churchData) {
          churchName = churchData.nome;
        }
      }
      
      setUserProfile({
        name: userName,
        email: userEmail,
        createdAt,
        churchName
      });
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
    }
  }

  async function fetchJourneyStats() {
    try {
      setLoading(true);
      
      // Em um ambiente real, estas informações viriam do banco de dados
      // Aqui estamos simulando dados para demonstração
      
      // Obter data de início com base no intervalo selecionado
      const today = new Date();
      let startDate = new Date();
      
      switch (timeRange) {
        case 'week':
          startDate.setDate(today.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(today.getMonth() - 1);
          break;
        case 'year':
          startDate.setFullYear(today.getFullYear() - 1);
          break;
        case 'all':
          // Usar a data de criação da conta ou um valor padrão
          startDate = new Date(2023, 0, 1); // 1º de janeiro de 2023
          break;
      }
      
      // Calcular dias no intervalo para ajustar os valores
      const daysInRange = Math.max(1, differenceInDays(today, startDate));
      const scaleFactor = daysInRange / 30; // Normalizar para um mês
      
      // Simular dados com base no intervalo
      const simulatedStats: JourneyStats = {
        totalInteractions: Math.floor(120 * scaleFactor),
        studyTime: Math.floor(840 * scaleFactor), // 14 horas em minutos
        prayerTime: Math.floor(420 * scaleFactor), // 7 horas em minutos
        challengesCompleted: Math.floor(5 * scaleFactor),
        totalAchievements: Math.floor(8 * scaleFactor),
        devotionalStreak: Math.min(Math.floor(21 * scaleFactor), 365), // Máximo de 365 dias
        communityEngagement: Math.floor(75 * scaleFactor / 3), // Valor percentual
        bibleReadingProgress: Math.min(Math.floor(35 * scaleFactor / 3), 100), // Valor percentual
        prayerRequests: Math.floor(12 * scaleFactor / 3),
        testimonies: Math.floor(3 * scaleFactor / 3),
        quizCompleted: Math.floor(8 * scaleFactor / 3),
        coursesCompleted: Math.floor(2 * scaleFactor / 6),
        smallGroupAttendance: Math.floor(4 * scaleFactor / 4),
        spiritualGrowthScore: Math.min(Math.floor(68 + (scaleFactor * 5)), 100) // Valor percentual
      };
      
      setStats(simulatedStats);
      
      // Simular conquistas
      const simulatedAchievements: Achievement[] = [
        {
          id: '1',
          title: 'Peregrino Dedicado',
          description: 'Completou 30 dias consecutivos de devocionais',
          icon: Flame,
          date: format(new Date(today.getTime() - 15 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          color: 'bg-amber-100 text-amber-600'
        },
        {
          id: '2',
          title: 'Estudioso da Palavra',
          description: 'Leu 10 capítulos da Bíblia em uma semana',
          icon: BookOpen,
          date: format(new Date(today.getTime() - 22 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          color: 'bg-blue-100 text-blue-600'
        },
        {
          id: '3',
          title: 'Intercessor',
          description: 'Fez 5 pedidos de oração',
          icon: Heart,
          date: format(new Date(today.getTime() - 45 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          color: 'bg-red-100 text-red-600'
        }
      ];
      
      // Filtrar conquistas com base no intervalo de tempo
      const filteredAchievements = simulatedAchievements.filter(achievement => {
        const achievementDate = new Date(achievement.date.split('/').reverse().join('-'));
        return achievementDate >= startDate;
      });
      
      setAchievements(filteredAchievements);
      
      // Simular atividades recentes
      const simulatedActivities: Activity[] = [
        {
          id: '1',
          type: 'bible',
          title: 'Leitura de Salmos 23',
          date: format(new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          icon: BookOpen,
          color: 'bg-blue-100 text-blue-600'
        },
        {
          id: '2',
          type: 'prayer',
          title: 'Pedido de oração enviado',
          date: format(new Date(today.getTime() - 5 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          icon: Heart,
          color: 'bg-red-100 text-red-600'
        },
        {
          id: '3',
          type: 'quiz',
          title: 'Quiz Bíblico completado',
          date: format(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          icon: Award,
          color: 'bg-purple-100 text-purple-600'
        },
        {
          id: '4',
          type: 'group',
          title: 'Participação em pequeno grupo',
          date: format(new Date(today.getTime() - 10 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          icon: Users,
          color: 'bg-green-100 text-green-600'
        },
        {
          id: '5',
          type: 'course',
          title: 'Aula de curso concluída',
          date: format(new Date(today.getTime() - 12 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy', { locale: ptBR }),
          icon: BookOpen,
          color: 'bg-indigo-100 text-indigo-600'
        }
      ];
      
      // Filtrar atividades com base no intervalo de tempo
      const filteredActivities = simulatedActivities.filter(activity => {
        const activityDate = new Date(activity.date.split('/').reverse().join('-'));
        return activityDate >= startDate;
      });
      
      setRecentActivities(filteredActivities);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  }

  function formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} min`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}min`;
    }
  }

  function getGrowthLevel(score: number): string {
    if (score >= 90) return 'Discípulo Maduro';
    if (score >= 75) return 'Discípulo Avançado';
    if (score >= 60) return 'Discípulo em Crescimento';
    if (score >= 40) return 'Discípulo Iniciante';
    return 'Novo na Jornada';
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Compass className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Minha Jornada de Fé</h1>
        </div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
        >
          <Home className="h-4 w-4" />
          <span>Voltar para Home</span>
        </button>
      </div>

      {/* Filtro de período */}
      <div className="mb-8 flex justify-end">
        <div className="inline-flex rounded-md shadow-sm">
          <button
            onClick={() => setTimeRange('week')}
            className={`px-4 py-2 text-sm font-medium ${
              timeRange === 'week'
                ? 'bg-brand-teal text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border border-gray-300 rounded-l-lg`}
          >
            Semana
          </button>
          <button
            onClick={() => setTimeRange('month')}
            className={`px-4 py-2 text-sm font-medium ${
              timeRange === 'month'
                ? 'bg-brand-teal text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border-t border-b border-r border-gray-300`}
          >
            Mês
          </button>
          <button
            onClick={() => setTimeRange('year')}
            className={`px-4 py-2 text-sm font-medium ${
              timeRange === 'year'
                ? 'bg-brand-teal text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border-t border-b border-r border-gray-300`}
          >
            Ano
          </button>
          <button
            onClick={() => setTimeRange('all')}
            className={`px-4 py-2 text-sm font-medium ${
              timeRange === 'all'
                ? 'bg-brand-teal text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border-t border-b border-r border-gray-300 rounded-r-lg`}
          >
            Tudo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-teal"></div>
        </div>
      ) : (
        <>
          {/* Resumo Espiritual */}
          <div className="bg-gradient-to-r from-brand-teal to-brand-teal/80 text-white p-6 rounded-xl shadow-elevation-2 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-2xl font-semibold mb-2">
                  Olá, {userProfile.name}! 👋
                </h2>
                <p className="text-white/90">
                  Sua jornada de fé começou em {userProfile.createdAt}. Continue crescendo!
                </p>
              </div>
              <div className="flex flex-col items-start md:items-end">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="h-4 w-4" />
                  <span>Nível: {getGrowthLevel(stats.spiritualGrowthScore)}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="h-4 w-4" />
                  <span>Crescimento Espiritual: {stats.spiritualGrowthScore}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <Flame className="h-4 w-4" />
                  <span>{stats.devotionalStreak} dias consecutivos de devoção</span>
                </div>
              </div>
            </div>
          </div>

          {/* Visão Geral */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Tempo de Estudo e Oração */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <span>Tempo Dedicado</span>
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-gray-900">Estudo Bíblico</span>
                    </div>
                    <span className="font-bold text-blue-600">{formatTime(stats.studyTime)}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (stats.studyTime / (60 * 20)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Heart className="h-5 w-5 text-red-600" />
                      <span className="font-medium text-gray-900">Tempo de Oração</span>
                    </div>
                    <span className="font-bold text-red-600">{formatTime(stats.prayerTime)}</span>
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (stats.prayerTime / (60 * 10)) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Progresso de Leitura Bíblica */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-indigo-600" />
                  <span>Leitura Bíblica</span>
                </h2>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="relative w-40 h-40 mb-4">
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle 
                      cx="50" 
                      cy="50" 
                      r="45" 
                      fill="none" 
                      stroke="#e0e7ff" 
                      strokeWidth="10" 
                    />
                    <circle 
                      cx="50" 
                      cy="50" 
                      r="45" 
                      fill="none" 
                      stroke="#4f46e5" 
                      strokeWidth="10" 
                      strokeDasharray={`${stats.bibleReadingProgress * 2.83} 283`} 
                      strokeDashoffset="0" 
                      transform="rotate(-90 50 50)" 
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-3xl font-bold text-indigo-600">{stats.bibleReadingProgress}%</span>
                      <p className="text-xs text-gray-500">Concluído</p>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <p className="text-gray-600">Você está lendo o plano:</p>
                  <p className="font-medium text-gray-900">Bíblia em 1 Ano</p>
                </div>
              </div>
            </div>
            
            {/* Engajamento Comunitário */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  <span>Engajamento</span>
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="text-green-600 font-semibold text-xl">{stats.smallGroupAttendance}</div>
                    <div className="text-xs text-gray-600">Encontros de Grupo</div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <div className="text-purple-600 font-semibold text-xl">{stats.prayerRequests}</div>
                    <div className="text-xs text-gray-600">Pedidos de Oração</div>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <div className="text-amber-600 font-semibold text-xl">{stats.testimonies}</div>
                    <div className="text-xs text-gray-600">Testemunhos</div>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-blue-600 font-semibold text-xl">{stats.communityEngagement}%</div>
                    <div className="text-xs text-gray-600">Engajamento</div>
                  </div>
                </div>
                
                <div className="text-center text-sm text-gray-600 mt-2">
                  <p>Igreja: {userProfile.churchName}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Conquistas e Desafios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Conquistas */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-600" />
                  <span>Conquistas</span>
                </h2>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                  {stats.totalAchievements} conquistas
                </span>
              </div>
              
              <div className="space-y-4">
                {achievements.length > 0 ? (
                  achievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`p-2 rounded-full ${achievement.color} shrink-0`}>
                        <achievement.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{achievement.title}</h3>
                        <p className="text-sm text-gray-600">{achievement.description}</p>
                        <p className="text-xs text-gray-500 mt-1">{achievement.date}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-6">
                    <Award className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nenhuma conquista no período selecionado</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Desafios */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  <span>Desafios</span>
                </h2>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                  {stats.challengesCompleted} concluídos
                </span>
              </div>
              
              <div className="space-y-4">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Desafio de Oração</h3>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                      Em andamento
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Ore por 10 minutos todos os dias durante uma semana</p>
                  <div className="mt-2 w-full bg-purple-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full" 
                      style={{ width: '71%' }}
                    ></div>
                  </div>
                  <p className="text-xs text-right mt-1 text-gray-500">5/7 dias</p>
                </div>
                
                <div className="p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Memorização de Versículos</h3>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                      Concluído
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Memorize 5 versículos sobre fé</p>
                  <div className="mt-2 w-full bg-green-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: '100%' }}
                    ></div>
                  </div>
                  <p className="text-xs text-right mt-1 text-gray-500">5/5 versículos</p>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">Leitura do Novo Testamento</h3>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Em andamento
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Leia todo o Novo Testamento em 3 meses</p>
                  <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: '35%' }}
                    ></div>
                  </div>
                  <p className="text-xs text-right mt-1 text-gray-500">35% concluído</p>
                </div>
              </div>
            </div>
          </div>

          {/* Atividades Recentes e Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Atividades Recentes */}
            <div className="md:col-span-2 bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-brand-teal" />
                  <span>Atividades Recentes</span>
                </h2>
                <span className="bg-brand-light text-brand-teal px-3 py-1 rounded-full text-sm font-medium">
                  {stats.totalInteractions} interações
                </span>
              </div>
              
              <div className="space-y-3">
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity) => (
                    <div key={activity.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${activity.color} shrink-0`}>
                          <activity.icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <h3 className="font-medium text-gray-900">{activity.title}</h3>
                            <span className="text-xs text-gray-500">{activity.date}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {activity.type === 'bible' && 'Leitura da Bíblia'}
                            {activity.type === 'prayer' && 'Oração'}
                            {activity.type === 'quiz' && 'Quiz Bíblico'}
                            {activity.type === 'group' && 'Pequeno Grupo'}
                            {activity.type === 'course' && 'Curso Bíblico'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Activity className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">Nenhuma atividade no período selecionado</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Estatísticas */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart2 className="h-5 w-5 text-indigo-600" />
                  <span>Estatísticas</span>
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-blue-600 font-semibold text-xl">{stats.quizCompleted}</div>
                    <div className="text-xs text-gray-600">Quizzes</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="text-green-600 font-semibold text-xl">{stats.coursesCompleted}</div>
                    <div className="text-xs text-gray-600">Cursos</div>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <div className="text-amber-600 font-semibold text-xl">{stats.devotionalStreak}</div>
                    <div className="text-xs text-gray-600">Dias Consecutivos</div>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <div className="text-purple-600 font-semibold text-xl">{stats.challengesCompleted}</div>
                    <div className="text-xs text-gray-600">Desafios</div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <h3 className="font-medium text-gray-900 mb-2">Crescimento Espiritual</h3>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                    <div 
                      className="bg-brand-teal h-2.5 rounded-full" 
                      style={{ width: `${stats.spiritualGrowthScore}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Iniciante</span>
                    <span>Intermediário</span>
                    <span>Avançado</span>
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-brand-light rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="h-5 w-5 text-brand-teal" />
                    <h3 className="font-medium text-brand-teal">Próximo Nível</h3>
                  </div>
                  <p className="text-sm text-gray-700">
                    Continue sua jornada! Faltam {100 - stats.spiritualGrowthScore} pontos para atingir o próximo nível.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Metas e Reflexões */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Metas */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Target className="h-5 w-5 text-red-600" />
                  <span>Metas Espirituais</span>
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-4 w-4 text-brand-teal" />
                    <h3 className="font-medium text-gray-900">Leitura Bíblica</h3>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Meta: Ler a Bíblia em 1 ano</span>
                    <span>{stats.bibleReadingProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-brand-teal h-2 rounded-full" 
                      style={{ width: `${stats.bibleReadingProgress}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-4 w-4 text-red-600" />
                    <h3 className="font-medium text-gray-900">Tempo de Oração</h3>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Meta: 30 minutos por dia</span>
                    <span>{Math.round((stats.prayerTime / (30 * timeRangeDays())) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (stats.prayerTime / (30 * timeRangeDays())) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <h3 className="font-medium text-gray-900">Participação Comunitária</h3>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Meta: 4 encontros por mês</span>
                    <span>{Math.round((stats.smallGroupAttendance / 4) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(100, (stats.smallGroupAttendance / 4) * 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Reflexões */}
            <div className="bg-white p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <PenTool className="h-5 w-5 text-purple-600" />
                  <span>Reflexões e Diário</span>
                </h2>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="h-5 w-5 text-purple-600" />
                    <h3 className="font-medium text-gray-900">Seu Diário Espiritual</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Registre suas experiências, orações respondidas e momentos especiais com Deus.
                  </p>
                  <button
                    onClick={() => navigate('/spiritual-counselor')}
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Escrever Nova Reflexão
                  </button>
                </div>
                
                <div className="p-4 bg-brand-light rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-5 w-5 text-brand-teal" />
                    <h3 className="font-medium text-brand-teal">Versículo do Dia</h3>
                  </div>
                  <p className="text-sm text-gray-700 italic mb-2">
                    "Porque Deus amou o mundo de tal maneira que deu o seu Filho unigênito, para que todo aquele que nele crê não pereça, mas tenha a vida eterna."
                  </p>
                  <p className="text-xs text-right text-brand-teal">João 3:16</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
  
  function timeRangeDays(): number {
    switch (timeRange) {
      case 'week': return 7;
      case 'month': return 30;
      case 'year': return 365;
      case 'all': return 365; // Valor padrão para cálculos
    }
  }
}